import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DocumentDetails from './DocumentDetails';
import AuditLog from './AuditLog';
import EditDocument from './EditDocument';
import axios from 'axios';
import './DocumentList.css';

const DocumentList = ({ reviews, enrichedDocuments, initialFilters, onFiltersCleared, onRefreshData }) => {
  const { token, isAuthenticated } = useAuth();
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedAuditDocumentId, setSelectedAuditDocumentId] = useState(null);
  const [selectedEditDocumentId, setSelectedEditDocumentId] = useState(null);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const defaultFilters = {
    documentId: '',
    documentTitle: '',
    reviewer: '',
    currentStatus: '',
    reviewDateFrom: '',
    reviewDateTo: '',
    comments: '',
    // Document attribute filters
    companyName: '',
    projectName: '',
    contractNo: '',
    payrollNo: '',
    payrollStartAfter: '',
    payrollStartBefore: '',
    payrollEndAfter: '',
    payrollEndBefore: '',
    employeeName: '',
    employeeJobTitle: '',
    employeeLast4SSN: ''
  };

  const [filters, setFilters] = useState(() => {
    // Always start with default filters, then merge initialFilters if provided
    return initialFilters ? { ...defaultFilters, ...initialFilters } : defaultFilters;
  });

  // Update filters when initialFilters prop changes
  useEffect(() => {
    if (initialFilters) {
      setFilters(prev => ({
        ...prev,
        ...initialFilters
      }));
    }
  }, [initialFilters]);

  // Periodic refresh to show real-time updates
  useEffect(() => {
    // Only set up periodic refresh if we have a refresh function, auto-refresh is enabled, and no modals are open
    const hasOpenModal = selectedDocumentId || selectedAuditDocumentId || selectedEditDocumentId;
    if (!onRefreshData || !autoRefreshEnabled || hasOpenModal) {
      return;
    }

    // Set up periodic refresh every 5 seconds to balance real-time updates with server load
    const refreshInterval = setInterval(async () => {
      try {
        setIsRefreshing(true);
        await onRefreshData();
      } catch (error) {
        console.error('Error during periodic refresh:', error);
      } finally {
        setIsRefreshing(false);
      }
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(refreshInterval);
  }, [onRefreshData, autoRefreshEnabled, selectedDocumentId, selectedAuditDocumentId, selectedEditDocumentId]);

  const [sortConfig, setSortConfig] = useState({
    key: 'review_date',
    direction: 'desc'
  });

  // Helper function to get proper authentication headers
  const getAuthHeaders = () => {
    // Priority: 1. User JWT token, 2. Environment variable, 3. Default fallback
    const authToken = (isAuthenticated() && token) 
      ? token 
      : (process.env.REACT_APP_API_AUTH_TOKEN || '12345');
      
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  // Helper function to get enriched document data for a review
  const getDocumentData = (review) => {
    if (!enrichedDocuments) return null;
    return enrichedDocuments.find(doc => doc.document_id === review.document_id);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Helper function to handle document link click
  const handleDocumentClick = (documentId) => {
    setSelectedDocumentId(documentId);
  };

  // Helper function to close document details modal
  const handleCloseModal = () => {
    setSelectedDocumentId(null);
  };

  // Helper function to handle audit log click
  const handleAuditClick = (documentId) => {
    setSelectedAuditDocumentId(documentId);
  };

  // Helper function to close audit modal
  const handleCloseAuditModal = () => {
    setSelectedAuditDocumentId(null);
  };

  // Helper function to handle preview click
  const handlePreviewClick = async (review) => {
    try {
      if (review.file_path) {
        // Use API preview endpoint for authenticated S3 access (relative URL for frontend proxy)
        const previewUrl = `/documents/${review.document_id}/preview`;

        // Fetch the file through the API with authentication
        const response = await fetch(previewUrl, {
          method: 'GET',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Preview error response:', errorText);
          throw new Error(`Preview failed: ${response.status} - ${errorText}`);
        }

        // Create blob and open in new window
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');

        if (!newWindow) {
          alert('Please allow pop-ups to preview documents');
        } else {
          // Clean up the object URL after a delay to allow the window to load
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        }
      } else {
        alert('File path not available for this document');
      }
    } catch (error) {
      console.error('Error previewing file:', error);
      alert('Error previewing file. Please try again.');
    }
  };

  // Helper function to handle edit click
  const handleEditClick = (documentId) => {
    setSelectedEditDocumentId(documentId);
  };

  // Handler for delete button
  const handleDeleteReview = async (reviewId, documentTitle) => {
    if (!window.confirm(`Are you sure you want to delete review #${reviewId} for document "${documentTitle || 'N/A'}"?\n\nNote: This will only delete the review record, not the document itself.`)) {
      return;
    }

    try {
      const response = await axios.delete(`/reviews/${reviewId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        alert(`Review ${reviewId} deleted successfully`);
        // Refresh the data to show the updated list
        if (onRefreshData) {
          onRefreshData();
        }
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert(`Failed to delete review: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Helper function to close edit modal
  const handleCloseEditModal = () => {
    setSelectedEditDocumentId(null);
    // Refresh the document list when the edit modal closes
    // This ensures any changes made are reflected
    window.location.reload();
  };

  // Helper function to handle download click
  const handleDownloadClick = async (review) => {
    try {
      if (review.file_path) {
        // Use API download endpoint for authenticated S3 access (relative URL for frontend proxy)
        const downloadUrl = `/documents/${review.document_id}/download`;

        // Fetch the file through the API
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Download error response:', errorText);
          throw new Error(`Download failed: ${response.status} - ${errorText}`);
        }

        // Get filename from Content-Disposition header or use fallback
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `document_${review.document_id}`;
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (matches && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        } else {
          // Fallback to extracting filename from file_path or document title
          if (review.file_path.includes('/')) {
            filename = review.file_path.split('/').pop();
          } else if (review.document_title) {
            filename = review.document_title + '.pdf';
          }
        }

        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        alert('File path not available for this document');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  // Helper function to handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Helper function to handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to toggle comment expansion
  const toggleCommentExpansion = (reviewId) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Helper function to get sortable value from review
  const getSortValue = (review, key) => {
    const docData = getDocumentData(review);

    switch (key) {
      case 'document_id':
        return review.document_id;
      case 'document_title':
        return review.document_title || '';
      case 'reviewer_id':
        return review.reviewer_id || '';
      case 'current_status':
        return review.current_status || review.new_status || '';
      case 'review_date':
        return new Date(review.review_date);
      case 'comments':
        return review.comments || '';
      case 'contractor_name':
        return docData?.contractor_name || docData?.subcontractor_name || '';
      case 'project_name':
        return docData?.project_name || '';
      case 'payroll_no':
        return docData?.payroll_no || 0;
      case 'payroll_start_date':
        return docData?.payroll_start_date || '';
      default:
        return '';
    }
  };

  // Helper function to clear all filters
  const handleClearFilters = () => {
    setFilters({ ...defaultFilters });
    
    // Call the callback to clear filters in parent component
    if (onFiltersCleared) {
      onFiltersCleared();
    }
  };

  // Get unique values for dropdown filters
  const uniqueReviewers = useMemo(() => {
    if (!reviews || reviews.length === 0) return [];
    const reviewers = [...new Set(reviews.map(r => r.reviewer_id))].filter(Boolean);
    return reviewers.sort();
  }, [reviews]);

  const uniqueStatuses = useMemo(() => {
    if (!reviews || reviews.length === 0) return [];
    const statuses = [...new Set(reviews.map(r => r.current_status || r.new_status))].filter(Boolean);
    return statuses.sort();
  }, [reviews]);

  // Filter and sort reviews based on current filters and sort config
  const filteredAndSortedReviews = useMemo(() => {
    if (!reviews || reviews.length === 0) return [];
    
    // First, group reviews by document_id and get the latest review for each document
    const latestReviewsByDocument = reviews.reduce((acc, review) => {
      const docId = review.document_id;
      if (!acc[docId] || new Date(review.review_date) > new Date(acc[docId].review_date)) {
        acc[docId] = review;
      }
      return acc;
    }, {});

    // Convert back to array of latest reviews only
    const latestReviews = Object.values(latestReviewsByDocument);
    
    // Then, filter the latest reviews
    const filtered = latestReviews.filter(review => {
      // Get enriched document data for this review
      const docData = getDocumentData(review);

      // Document ID filter
      if (filters.documentId && !review.document_id.toString().includes(filters.documentId)) {
        return false;
      }

      // Document Title filter
      if (filters.documentTitle && review.document_title && !review.document_title.toLowerCase().includes(filters.documentTitle.toLowerCase())) {
        return false;
      }

      // Reviewer filter
      if (filters.reviewer && review.reviewer_id !== filters.reviewer) {
        return false;
      }

      // Current Status filter
      if (filters.currentStatus && (review.current_status || review.new_status) !== filters.currentStatus) {
        return false;
      }

      // Review Date From filter
      if (filters.reviewDateFrom) {
        const reviewDate = new Date(review.review_date);
        const fromDate = new Date(filters.reviewDateFrom);
        if (reviewDate < fromDate) {
          return false;
        }
      }

      // Review Date To filter
      if (filters.reviewDateTo) {
        const reviewDate = new Date(review.review_date);
        const toDate = new Date(filters.reviewDateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (reviewDate > toDate) {
          return false;
        }
      }

      // Comments filter
      if (filters.comments) {
        if (!review.comments || !review.comments.toLowerCase().includes(filters.comments.toLowerCase())) {
          return false;
        }
      }


      // Document attribute filters (only apply if enriched data is available)
      if (docData) {
        // Company Name filter (searches both contractor and subcontractor fields)
        if (filters.companyName) {
          const searchTerm = filters.companyName.toLowerCase();
          const contractorMatch = docData.contractor_name && docData.contractor_name.toLowerCase().includes(searchTerm);
          const subcontractorMatch = docData.subcontractor_name && docData.subcontractor_name.toLowerCase().includes(searchTerm);

          if (!contractorMatch && !subcontractorMatch) {
            return false;
          }
        }

        // Project Name filter
        if (filters.projectName && (!docData.project_name || !docData.project_name.toLowerCase().includes(filters.projectName.toLowerCase()))) {
          return false;
        }

        // Contract Number filter
        if (filters.contractNo && (!docData.contract_no || !docData.contract_no.toLowerCase().includes(filters.contractNo.toLowerCase()))) {
          return false;
        }

        // Payroll Number filter
        if (filters.payrollNo && (!docData.payroll_no || docData.payroll_no.toString() !== filters.payrollNo)) {
          return false;
        }

        // Payroll Start Date filters
        if (filters.payrollStartAfter && (!docData.payroll_start_date || docData.payroll_start_date < filters.payrollStartAfter)) {
          return false;
        }

        if (filters.payrollStartBefore && (!docData.payroll_start_date || docData.payroll_start_date > filters.payrollStartBefore)) {
          return false;
        }

        // Payroll End Date filters
        if (filters.payrollEndAfter && (!docData.payroll_end_date || docData.payroll_end_date < filters.payrollEndAfter)) {
          return false;
        }

        if (filters.payrollEndBefore && (!docData.payroll_end_date || docData.payroll_end_date > filters.payrollEndBefore)) {
          return false;
        }

        // Employee Name filter (would need to search in employee data)
        if (filters.employeeName) {
          // This is a simplified check - in a real implementation, you'd want to search the employees array
          const searchTerm = filters.employeeName.toLowerCase();
          if (!review.document_title || !review.document_title.toLowerCase().includes(searchTerm)) {
            return false;
          }
        }

        // Employee Job Title filter
        if (filters.employeeJobTitle) {
          // This is a simplified check - in a real implementation, you'd want to search the employees array
          const searchTerm = filters.employeeJobTitle.toLowerCase();
          if (!review.document_title || !review.document_title.toLowerCase().includes(searchTerm)) {
            return false;
          }
        }

        // Employee Last 4 SSN filter
        if (filters.employeeLast4SSN) {
          // This is a simplified check - in a real implementation, you'd want to search the employees array
          if (!review.document_title || !review.document_title.includes(filters.employeeLast4SSN)) {
            return false;
          }
        }
      }

      return true;
    });

    // Then, sort the filtered results
    return filtered.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [reviews, filters, enrichedDocuments, sortConfig]);

  // Early return check after all hooks have been called
  if (!reviews || reviews.length === 0) {
    return <div className="no-documents">No reviews found</div>;
  }

  return (
    <div className="document-list">
      <h2>Document Reviews ({filteredAndSortedReviews.length} unique documents)</h2>
      <p className="list-description">
        Showing the latest review status for each unique document. Re-uploaded documents appear only once with their most recent status.
      </p>

      {/* Filter Controls */}
      <div className="filter-controls">
        <div className="filter-row">
          <div className="filter-group">
            <label>Document ID:</label>
            <input
              type="text"
              placeholder="Filter by Document ID"
              value={filters.documentId}
              onChange={(e) => handleFilterChange('documentId', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Document Title:</label>
            <input
              type="text"
              placeholder="Filter by Document Title"
              value={filters.documentTitle}
              onChange={(e) => handleFilterChange('documentTitle', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Reviewer:</label>
            <select
              value={filters.reviewer}
              onChange={(e) => handleFilterChange('reviewer', e.target.value)}
            >
              <option value="">All Reviewers</option>
              {uniqueReviewers.map(reviewer => (
                <option key={reviewer} value={reviewer}>{reviewer}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Current Status:</label>
            <select
              value={filters.currentStatus}
              onChange={(e) => handleFilterChange('currentStatus', e.target.value)}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-row">

          <div className="filter-group">
            <label>Review Date From:</label>
            <input
              type="date"
              value={filters.reviewDateFrom}
              onChange={(e) => handleFilterChange('reviewDateFrom', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Review Date To:</label>
            <input
              type="date"
              value={filters.reviewDateTo}
              onChange={(e) => handleFilterChange('reviewDateTo', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Comments:</label>
            <input
              type="text"
              placeholder="Search in comments"
              value={filters.comments}
              onChange={(e) => handleFilterChange('comments', e.target.value)}
            />
          </div>
        </div>

        {/* Document Attribute Filters */}
        <div className="filter-section-header">
          <h3>Document Attributes</h3>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Company Name:</label>
            <input
              type="text"
              placeholder="Filter by contractor or subcontractor"
              value={filters.companyName}
              onChange={(e) => handleFilterChange('companyName', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Project Name:</label>
            <input
              type="text"
              placeholder="Filter by project"
              value={filters.projectName}
              onChange={(e) => handleFilterChange('projectName', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Contract No:</label>
            <input
              type="text"
              placeholder="Filter by contract #"
              value={filters.contractNo}
              onChange={(e) => handleFilterChange('contractNo', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Payroll No:</label>
            <input
              type="number"
              placeholder="Payroll number"
              value={filters.payrollNo}
              onChange={(e) => handleFilterChange('payrollNo', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Payroll Start After:</label>
            <input
              type="date"
              value={filters.payrollStartAfter}
              onChange={(e) => handleFilterChange('payrollStartAfter', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Payroll Start Before:</label>
            <input
              type="date"
              value={filters.payrollStartBefore}
              onChange={(e) => handleFilterChange('payrollStartBefore', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Payroll End After:</label>
            <input
              type="date"
              value={filters.payrollEndAfter}
              onChange={(e) => handleFilterChange('payrollEndAfter', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Payroll End Before:</label>
            <input
              type="date"
              value={filters.payrollEndBefore}
              onChange={(e) => handleFilterChange('payrollEndBefore', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Employee Name:</label>
            <input
              type="text"
              placeholder="Employee first/last name"
              value={filters.employeeName}
              onChange={(e) => handleFilterChange('employeeName', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Employee Job Title:</label>
            <input
              type="text"
              placeholder="Job title/classification"
              value={filters.employeeJobTitle}
              onChange={(e) => handleFilterChange('employeeJobTitle', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Employee Last 4 SSN:</label>
            <input
              type="number"
              placeholder="Last 4 digits"
              value={filters.employeeLast4SSN}
              onChange={(e) => handleFilterChange('employeeLast4SSN', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              Clear All Filters
            </button>
          </div>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="sort-controls">
        <div className="sort-info">
          <strong>Sorted by:</strong> {sortConfig.key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          ({sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'})
        </div>
        <div className="sort-buttons">
          <button
            className={`sort-btn ${autoRefreshEnabled ? 'active' : 'inactive'}`}
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            title={autoRefreshEnabled ? 'Disable auto-refresh (5s)' : 'Enable auto-refresh (5s)'}
            style={{ marginRight: '10px' }}
          >
            Auto-refresh {autoRefreshEnabled ? '🔄' : '⏸️'}
          </button>
          <button
            className="sort-btn"
            onClick={() => handleSort('review_date')}
            title="Sort by review date"
          >
            Sort by Date {sortConfig.key === 'review_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className="sort-btn"
            onClick={() => handleSort('current_status')}
            title="Sort by status"
          >
            Sort by Status {sortConfig.key === 'current_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className="sort-btn"
            onClick={() => handleSort('document_id')}
            title="Sort by document ID"
          >
            Sort by ID {sortConfig.key === 'document_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </button>
          <button
            className="sort-btn"
            onClick={() => handleSort('contractor_name')}
            title="Sort by contractor name"
          >
            Sort by Contractor {sortConfig.key === 'contractor_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </button>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th
              className={`sortable ${sortConfig.key === 'document_id' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('document_id')}
            >
              Document ID {sortConfig.key === 'document_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'document_title' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('document_title')}
            >
              Document Title {sortConfig.key === 'document_title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'contractor_name' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('contractor_name')}
            >
              Contractor/Subcontractor {sortConfig.key === 'contractor_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'project_name' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('project_name')}
            >
              Project {sortConfig.key === 'project_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'payroll_no' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('payroll_no')}
            >
              Payroll # {sortConfig.key === 'payroll_no' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'payroll_start_date' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('payroll_start_date')}
            >
              Payroll Period {sortConfig.key === 'payroll_start_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'reviewer_id' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('reviewer_id')}
            >
              Reviewer {sortConfig.key === 'reviewer_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'current_status' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('current_status')}
            >
              Current Status {sortConfig.key === 'current_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'review_date' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('review_date')}
            >
              Last Review Date {sortConfig.key === 'review_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th
              className={`sortable ${sortConfig.key === 'comments' ? `sorted-${sortConfig.direction}` : ''}`}
              onClick={() => handleSort('comments')}
            >
              Comments {sortConfig.key === 'comments' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedReviews.map((review) => {
            const docData = getDocumentData(review);
            return (
              <tr key={review.review_id} className={`status-${(review.current_status || review.new_status).toLowerCase()}`}>
                <td>
                  <button
                    className="document-link"
                    onClick={() => handleDocumentClick(review.document_id)}
                    title="Click to view document details"
                  >
                    {review.document_id}
                  </button>
                </td>
                <td>{review.document_title || 'N/A'}</td>
                <td>
                  {docData ? (
                    <>
                      {docData.contractor_name && <div><strong>C:</strong> {docData.contractor_name}</div>}
                      {docData.subcontractor_name && <div><strong>S:</strong> {docData.subcontractor_name}</div>}
                      {!docData.contractor_name && !docData.subcontractor_name && 'N/A'}
                    </>
                  ) : 'N/A'}
                </td>
                <td>
                  {docData ? (
                    <>
                      {docData.project_name && <div>{docData.project_name}</div>}
                      {docData.contract_no && <div><small>#{docData.contract_no}</small></div>}
                      {!docData.project_name && !docData.contract_no && 'N/A'}
                    </>
                  ) : 'N/A'}
                </td>
                <td>{docData?.payroll_no || 'N/A'}</td>
                <td>
                  {docData && docData.payroll_start_date && docData.payroll_end_date ? (
                    <div>
                      <div>{docData.payroll_start_date}</div>
                      <div>to {docData.payroll_end_date}</div>
                    </div>
                  ) : 'N/A'}
                </td>
                <td>{review.reviewer_id}</td>
                <td className="status-cell">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(() => {
                      const status = review.current_status || review.new_status;
                      const isProcessing = status === 'processing' || status === 'verifying';
                      return (
                        <>
                          {isProcessing && (
                            <span 
                              className="processing-spinner" 
                              title="Document is being processed"
                              style={{
                                display: 'inline-block',
                                width: '14px',
                                height: '14px',
                                border: '2px solid #f3f3f3',
                                borderTop: '2px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          )}
                          <span className={`status-badge ${status?.toLowerCase()}`}>
                            {status}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </td>
                <td>{formatDate(review.review_date)}</td>
                <td className="comments-cell">
                  {review.comments ? (
                    <div className="collapsible-comments">
                      <div className="comments-preview">
                        <span className="comments-text">
                          {expandedComments.has(review.review_id)
                            ? review.comments
                            : `${review.comments.substring(0, 50)}${review.comments.length > 50 ? '...' : ''}`
                          }
                        </span>
                        {review.comments.length > 50 && (
                          <button
                            className="comments-toggle"
                            onClick={() => toggleCommentExpansion(review.review_id)}
                            title={expandedComments.has(review.review_id) ? 'Show less' : 'Show more'}
                          >
                            {expandedComments.has(review.review_id) ? '▲' : '▼'}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : 'N/A'}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn preview-btn"
                      onClick={() => handlePreviewClick(review)}
                      title="Preview document"
                    >
                      Preview
                    </button>
                    <button
                      className="action-btn edit-btn"
                      onClick={() => handleEditClick(review.document_id)}
                      title="Review document metadata"
                    >
                      Review
                    </button>
                    <button
                      className="action-btn download-btn"
                      onClick={() => handleDownloadClick(review)}
                      title="Download document file"
                    >
                      Download
                    </button>
                    <button
                      className="action-btn audit-btn"
                      onClick={() => handleAuditClick(review.document_id)}
                      title="View status change history"
                    >
                      Audit
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteReview(review.review_id, review.document_title)}
                      title="Delete this review"
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        marginLeft: '4px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectedDocumentId && (
        <DocumentDetails
          documentId={selectedDocumentId}
          onClose={handleCloseModal}
        />
      )}

      {selectedAuditDocumentId && (
        <AuditLog
          documentId={selectedAuditDocumentId}
          onClose={handleCloseAuditModal}
        />
      )}

      {selectedEditDocumentId && (
        <EditDocument
          documentId={selectedEditDocumentId}
          onClose={handleCloseEditModal}
          onDocumentUpdated={async () => {
            // Refresh data after document is updated (especially after approval)
            console.log('Document updated - refreshing document list');
            if (onRefreshData) {
              try {
                await onRefreshData();
              } catch (error) {
                console.error('Error refreshing data after document update:', error);
              }
            }
          }}
        />
      )}

    </div>
  );
};

export default DocumentList;